/**
 * Operational logger for the SPA — the durable, non-audit companion to the
 * `audit_log`. Every call mirrors to the browser console and, for high-enough
 * levels, is persisted to the `app_log` table via the `write_log` RPC.
 *
 * Two independent thresholds:
 *   - console:  `VITE_LOG_LEVEL`         (default "info")  — what you see locally
 *   - persist:  `VITE_LOG_PERSIST_LEVEL` (default "warn")  — what reaches the DB
 *
 * So by default warn+error are stored and info/debug are console-only; lower
 * `VITE_LOG_PERSIST_LEVEL` to "info"/"debug" to capture verbose traces while
 * debugging a live issue.
 *
 * Contract: logging must NEVER throw and must NEVER block. Persistence is
 * best-effort and fire-and-forget; a failed write degrades to a console warning
 * and is otherwise swallowed. Callers must not pass secrets/PII in `context`.
 */

import { writeLog } from "@/api/logs";
import type { Json } from "@/integrations/supabase/types";

export type LogLevel = "debug" | "info" | "warn" | "error";

export type LogContext = Record<string, unknown>;

const LEVELS: Record<LogLevel, number> = {
  debug: 10,
  info: 20,
  warn: 30,
  error: 40,
};

function parseLevel(value: unknown, fallback: LogLevel): LogLevel {
  const v = typeof value === "string" ? value.toLowerCase() : "";
  return v in LEVELS ? (v as LogLevel) : fallback;
}

const env = import.meta.env;
const CONSOLE_THRESHOLD = LEVELS[parseLevel(env.VITE_LOG_LEVEL, "info")];
const PERSIST_THRESHOLD = LEVELS[parseLevel(env.VITE_LOG_PERSIST_LEVEL, "warn")];
const RELEASE: string | undefined = env.VITE_APP_VERSION;
const SOURCE = "client";

// De-dupe identical entries so a render loop or repeated failure can't flood the
// DB. Same (level + message) within the window is persisted at most once.
const DEDUPE_WINDOW_MS = 5000;
// Hard cap so a stream of unique messages (e.g. ones carrying dynamic ids) can't
// grow the map without bound in a long-lived SPA session.
const DEDUPE_MAX_ENTRIES = 500;
const lastPersisted = new Map<string, number>();

const consoleMethod: Record<LogLevel, (...args: unknown[]) => void> = {
  debug: console.debug.bind(console),
  info: console.info.bind(console),
  warn: console.warn.bind(console),
  error: console.error.bind(console),
};

/** Flatten an Error (or anything) into a JSON-serialisable context object. */
export function serializeError(err: unknown): LogContext {
  // PostgREST/Supabase errors carry code/details/hint beside the message.
  const extra: LogContext = {};
  if (err && typeof err === "object") {
    for (const key of ["code", "details", "hint", "status"] as const) {
      const v = (err as Record<string, unknown>)[key];
      if (v != null) extra[key] = v;
    }
  }
  if (err instanceof Error) {
    return { name: err.name, message: err.message, stack: err.stack, ...extra };
  }
  if (err && typeof err === "object" && typeof (err as { message?: unknown }).message === "string") {
    return { message: (err as { message: string }).message, ...extra };
  }
  return { value: String(err) };
}

/** Best-effort JSON-safe copy so a non-serialisable context can't break a write. */
function safeContext(context?: LogContext): LogContext {
  if (!context) return {};
  try {
    return JSON.parse(JSON.stringify(context)) as LogContext;
  } catch {
    return { _unserializable: true };
  }
}

function shouldPersist(level: LogLevel, message: string): boolean {
  if (LEVELS[level] < PERSIST_THRESHOLD) return false;
  const key = `${level}:${message}`;
  const now = Date.now();
  const prev = lastPersisted.get(key);
  if (prev !== undefined && now - prev < DEDUPE_WINDOW_MS) return false;
  // Keep the map bounded: once it hits the cap, drop entries whose de-dupe window
  // has already expired; if that isn't enough, clear it outright (dedupe simply
  // resets — cheaper than an unbounded leak).
  if (lastPersisted.size >= DEDUPE_MAX_ENTRIES) {
    for (const [k, t] of lastPersisted) {
      if (now - t >= DEDUPE_WINDOW_MS) lastPersisted.delete(k);
    }
    if (lastPersisted.size >= DEDUPE_MAX_ENTRIES) lastPersisted.clear();
  }
  lastPersisted.set(key, now);
  return true;
}

function persist(level: LogLevel, message: string, context: LogContext): void {
  // Fire-and-forget; never let a logging failure surface to the caller.
  try {
    void writeLog({
      p_level: level,
      p_message: message,
      p_context: context as Json,
      p_source: SOURCE,
      p_url: typeof window !== "undefined" ? window.location.href : null,
      p_user_agent:
        typeof navigator !== "undefined" ? navigator.userAgent : null,
      p_release: RELEASE ?? null,
    }).then(
      ({ error }) => {
        if (error) console.warn("[logger] write_log failed:", error.message);
      },
      (e: unknown) => console.warn("[logger] write_log rejected:", e),
    );
  } catch (e) {
    console.warn("[logger] write_log threw:", e);
  }
}

function emit(level: LogLevel, message: string, context?: LogContext): void {
  try {
    const safe = safeContext(context);
    if (LEVELS[level] >= CONSOLE_THRESHOLD) {
      consoleMethod[level](`[${level}] ${message}`, safe);
    }
    if (shouldPersist(level, message)) {
      persist(level, message, safe);
    }
  } catch {
    // A logger that throws is worse than a missing log line.
  }
}

export const logger = {
  debug: (message: string, context?: LogContext) =>
    emit("debug", message, context),
  info: (message: string, context?: LogContext) =>
    emit("info", message, context),
  warn: (message: string, context?: LogContext) =>
    emit("warn", message, context),
  error: (message: string, context?: LogContext) =>
    emit("error", message, context),
  log: (level: LogLevel, message: string, context?: LogContext) =>
    emit(level, message, context),
};

/**
 * Install global handlers so uncaught exceptions and unhandled promise
 * rejections are captured "in the first place". Idempotent; safe to call once
 * at app start.
 */
let handlersInstalled = false;
export function installGlobalErrorHandlers(): void {
  if (handlersInstalled || typeof window === "undefined") return;
  handlersInstalled = true;

  window.addEventListener("error", (event: ErrorEvent) => {
    logger.error("Uncaught error", {
      ...serializeError(event.error ?? event.message),
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    });
  });

  window.addEventListener(
    "unhandledrejection",
    (event: PromiseRejectionEvent) => {
      logger.error("Unhandled promise rejection", serializeError(event.reason));
    },
  );
}
